/**
 * usePlanState — owns draft PLANS: a roster of slots where each slot holds a
 * fixed four-wide row of targets instead of a single player.
 *
 * Standalone in the same sense useScout is: it imports no other hook and owns
 * its own localStorage. It takes (settings, rosterSlots, dynamicPrices) and
 * returns plan state plus the derived budget math.
 *
 * THE CONCEPT. useBuilderState.roster is `{ [slotId]: player }` — one player per
 * slot. A plan is `{ [slotId]: bucket }`, and a bucket is a fixed four-cell row:
 * primary, backup 1, backup 2, backup 3. Only cell 0 counts against the budget.
 * Promoting a backup is a swap of cell 0 with cell N, and that swap is the whole
 * product.
 *
 * A FIXED FOUR, NOT AN UNBOUNDED LIST. The row layout is the feature — a grid
 * you read across at a glance beats a variable-length list you have to scan —
 * and four is the width of a real tier ("three or four names in the $30 WR
 * bucket"). Verified to hold without truncation or horizontal scroll down to a
 * 1512px viewport.
 *
 *   Target = { key, name, pos, team, market, price, edited, gone, locked }
 *   Bucket = { budget: number|null, cells: [Target|null x4] }
 *   Plan   = { id, name, budget, objective, buckets: { [slotId]: Bucket } }
 *
 * A bucket's `budget` EARMARKS money for the slot before anyone is picked for
 * it, so a plan reports both `planned` (Σ earmarks) and `targeted` (Σ what the
 * listed primaries cost). `null` means "follow the primary" — see slotBudget.
 *
 * The pure helpers below (primaryOf / planTotals / promoteInBucket /
 * toggleGoneInBucket) carry all the math and are exported so planBudget.test.js
 * can pin it without rendering a component.
 *
 * 🔴 THIS HOOK ALSO OWNS THE PRICE SHEET, AND THE SHEET IS NOT THE BOARD.
 * A per-league map of playerKey → the price YOU say a player costs, for any
 * player, in a plan or not. It NEVER writes to dynamicPrices: usePricing still
 * owns the board, and the bid AI, the mock draft, PlayerCard and Team Builder
 * all keep reading one unchanged number. The sheet is a planning overlay read
 * only by Draft Plan and its solver. That separation is what made this safe to
 * build and is exactly what the removed customValues feature got wrong — see
 * the section header further down before "unifying" the two.
 *
 * THREE LEAGUE-SCOPED SHEETS, and they stay three. priceSheet and ppgSheet
 * hold NUMBERS the solver reads; starSheet holds a FLAG it never does. Same
 * `{ [league]: { [playerKey]: value } }` shape, same persistence, separate
 * clear controls — see the note above each useState for why merging any two
 * of them is a mistake.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { capturePlanCreated } from "../utils/planAnalytics";

export const PLANS_KEY = "auctionArchitect_draftPlans";
export const ACTIVE_PLAN_KEY = "auctionArchitect_activePlanId";
export const PRICE_SHEET_KEY = "auctionArchitect_priceSheet";
export const PPG_SHEET_KEY = "auctionArchitect_ppgSheet";
export const STAR_SHEET_KEY = "auctionArchitect_starSheet";

// The row width IS the feature — see the header. Four cells, always.
export const BUCKET_WIDTH = 4;

export const emptyBucket = () => ({ budget: null, cells: [null, null, null, null] });

/**
 * What one open bench slot is assumed to cost.
 *
 * 🔴 NOT A GUESS AND NOT A PREFERENCE — it is optimizePlan's own reserve, which
 * holds back exactly $1 per open bench slot before it solves the starters
 * (`benchReserve = benchSlots.length`). Two places speak for that reserve now:
 * the PER SPOT seed below and the muted default on an empty bench rail in
 * SlotRow. Both read this, so the rail can never say one thing while the solver
 * does another.
 */
export const BENCH_RESERVE = 1;

/**
 * 🔴 THE OBJECTIVE IS A SETTING, NOT AN ARGUMENT — the whole point of the
 * toolbar split. The buttons used to carry BOTH axes at once (which objective,
 * and whether your picks survive), so every new combination wanted a new
 * button and the names had to encode two things. Objective is picked once and
 * rarely changed, so it lives on the plan; scope is the action, so it stays on
 * the buttons. Neither button takes an objective any more.
 *
 * Per PLAN rather than per league, deliberately: comparing a PPG plan against a
 * VORP plan side by side in the picker is a thing people do, and a league-wide
 * setting would make the two plans silently disagree with their own labels.
 */
export const OBJECTIVES = ["ppg", "vorp"];
export const DEFAULT_OBJECTIVE = "ppg";

/** A plan's objective, defaulting for every plan stored before it existed. */
export const planObjective = (plan) => {
  const raw = plan && typeof plan.objective === "string" ? plan.objective.toLowerCase() : null;
  return OBJECTIVES.includes(raw) ? raw : DEFAULT_OBJECTIVE;
};

/**
 * 🔴 THE SECOND AXIS, BESIDE THE OBJECTIVE AND FOR THE SAME REASON. The
 * objective says what to maximise; this says what the money constraint IS.
 *
 *   total — one pot. The per-slot rail figures stay display only, exactly as
 *           they were before this existed. THIS IS THE DEFAULT, and the
 *           optimizer's total-mode path is untouched by this feature.
 *   slot  — a slot carrying an explicit rail budget may only take a candidate
 *           priced at or below it. Slots with no explicit budget stay bound
 *           only by the total.
 *
 * Per plan rather than per league, for the reason above: two plans compared
 * side by side must each carry the rules they were solved under.
 */
export const BUDGET_MODES = ["total", "slot"];
export const DEFAULT_BUDGET_MODE = "total";

/** A plan's budget mode, defaulting for every plan stored before it existed. */
export const planBudgetMode = (plan) => {
  const raw = plan && typeof plan.budgetMode === "string" ? plan.budgetMode.toLowerCase() : null;
  return BUDGET_MODES.includes(raw) ? raw : DEFAULT_BUDGET_MODE;
};

/**
 * The name a generated plan wears, minus its parenthetical.
 *
 * 🔴 IT EXISTS SO nextPlanName CAN STAY UNCHANGED. That function answers "which
 * letters are free" by exact name match, and a generated plan is called
 * "Plan C (PPG, rebuilt)" — which takes the letter C on screen while matching
 * nothing in the taken set, so the next plan would also be offered C. Stripping
 * the parenthetical before the check keeps one function and one rule.
 */
export const planBaseName = (name) => String(name || "").replace(/\s*\([^()]*\)\s*$/, "").trim();

/**
 * An unguessable-enough plan id. Plans never leave the device, so this only has
 * to avoid collisions across imports, not attackers.
 */
function newPlanId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `plan-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// A target's key is the composite `${name}-${pos}` — the SAME key dynamicPrices
// uses (usePricing.js). Bare-name keys silently miss and read $1; see CLAUDE.md.
export const targetKey = (player) => `${player.name}-${player.pos}`;

/** Build a Target from a player, seeding market AND price off the live board. */
export function makeTarget(player, dynamicPrices) {
  const key = targetKey(player);
  const market = (dynamicPrices && dynamicPrices.get(key)) ?? 1;
  return {
    key,
    name: player.name,
    pos: player.pos,
    team: player.team ?? null,
    market,
    price: market,
    edited: false,
    gone: false,
    locked: false,
  };
}

/**
 * The one primary reader. cells[0] when it exists and is !gone, otherwise the
 * first !gone cell, otherwise null.
 *
 * EVERY other derived value goes through this function; never read cells[0]
 * directly anywhere else.
 *
 * 🔴 THE MEANING OF THE LATER CELLS CHANGED, AND THE FALL-THROUGH SURVIVED IT.
 * Read this before deciding the behaviour is a bug.
 *
 *   ORIGINALLY: cells 1-3 were BACKUPS — other candidates for this same slot,
 *   listed cheapest-alternative-first. Falling through to cell 1 meant "you
 *   lost him, here is the next name you wrote down for this slot", which is
 *   obviously right.
 *
 *   NOW: cells 1-3 are PLAN B / C / D — the same slot's occupant in three whole
 *   alternative rosters, each priced inside that roster's own budget. Falling
 *   through therefore mixes plans: the budget starts charging you for Plan B's
 *   pick at this slot while every other slot still shows Plan A's.
 *
 * The fall-through is STILL correct under the new meaning, for the same reason
 * it was under the old one. The moment you mark your guy gone at a live
 * auction you are mixing plans by definition — that IS drafting — and Plan B's
 * pick at that same slot is the most sensible thing that can happen without
 * asking. The alternative, going null, would drop the slot out of the budget
 * entirely and quietly overstate what you have left.
 *
 * The budget consequence is self-reporting rather than hidden: Plan B's pick
 * was costed inside Plan B's allocation, so LEFT can go negative and the danger
 * banner fires. And on an optimizer-generated plan the affected slot offers
 * "Plan B pick — re-optimize?", because re-solving takes 7ms and is the
 * actually-correct response to losing a target.
 */
export function primaryOf(bucket) {
  if (!bucket || !Array.isArray(bucket.cells)) return null;
  const first = bucket.cells[0];
  if (first && !first.gone) return first;
  return bucket.cells.find(c => c && !c.gone) || null;
}

/**
 * Swap cells[0] with cells[cellIdx] and clear `gone` on the promoted target.
 *
 * 🔴 Promoting an EMPTY cell is a no-op, not a swap. Without that guard the
 * swap reads undefined out of the target cell, writes it into cell 0 as null,
 * and shoves the real primary down the row — the user clicks promote on a blank
 * and their primary silently disappears from the slot. Normalizing the bucket
 * does NOT fix this: [A,B,C] and [A,B,C,null] both promote(3) to [null,B,C,A].
 * Padding fixes the row's LENGTH; this guard fixes the shuffle.
 */
export function promoteInBucket(bucket, cellIdx) {
  if (!bucket || cellIdx <= 0 || cellIdx >= BUCKET_WIDTH) return bucket;
  if (!bucket.cells || !bucket.cells[cellIdx]) return bucket;
  const cells = bucket.cells.slice();
  const promoted = cells[cellIdx];
  cells[cellIdx] = cells[0];
  cells[0] = promoted ? { ...promoted, gone: false } : null;
  return { ...bucket, cells };
}

/**
 * Flip `gone` on one cell. No other bookkeeping: primaryOf handles it.
 *
 * 🔴 A NO-OP ON A LOCKED CELL, and that is a semantic guard rather than a
 * defensive one. `gone` means "someone else won him" and `locked` means "I won
 * him"; both at once is not a state a real auction can produce. Refusing here
 * rather than in the component keeps the impossible state unreachable from the
 * hook, from a restored plan and from a future call site alike — and the ⊘
 * button is hidden on a locked card, so nobody meets this by clicking.
 */
export function toggleGoneInBucket(bucket, cellIdx) {
  if (!bucket || !bucket.cells[cellIdx]) return bucket;
  if (bucket.cells[cellIdx].locked) return bucket;
  const cells = bucket.cells.slice();
  cells[cellIdx] = { ...cells[cellIdx], gone: !cells[cellIdx].gone };
  return { ...bucket, cells };
}

// ── Locks ───────────────────────────────────────────────────────────────────
//
// 🔴 LOCK IS THE MIRROR OF GONE, AND IT IS A DRAFT-DAY CONTROL BEFORE IT IS A
// SOLVER HINT. `gone` says someone else won him; `locked` says I won him, at
// this price. That is why it lives on the TARGET and not in a league-scoped
// sheet beside the stars: a star expresses a view about a player and follows
// him everywhere, but "I own him at $49" is a fact about ONE plan's roster, and
// Plan A and Plan B have to be able to disagree about it.
//
// Four consequences, all of which fall out of the meaning rather than being
// chosen:
//   • It applies to the PRIMARY only — if you won him he is your guy at this
//     slot — so locking a backup promotes it first.
//   • It clears `gone`, and `gone` cannot be set on it afterwards.
//   • It PINS the price. resyncMarket and applySheetToPlans both rewrite an
//     un-edited target's price when the board or the sheet moves; a locked
//     price is what you actually paid, and a live auction rewriting it under
//     you is the one failure here nobody would catch in time.
//   • Unlocking restores ordinary behaviour but NOT the pre-lock price. The
//     lock was a statement about money; taking it back does not un-say it.

/** Is this cell locked? The one reader — never test `target.locked` directly. */
export const isLockedTarget = (t) => !!(t && t.locked);

/** Does this slot hold a locked primary? */
export function isLockedBucket(bucket) {
  return isLockedTarget(primaryOf(bucket));
}

/**
 * Lock — or unlock — one cell.
 *
 * Locking a cell that is not cell 0 PROMOTES it first, so the lock and the
 * budget can never disagree about who occupies the slot. Locking a `gone` cell
 * 0 un-goes it for the same reason: the two flags are mutually exclusive and
 * the newer statement wins.
 *
 * Locking an EMPTY cell is a no-op, exactly as promoting one is.
 */
export function toggleLockInBucket(bucket, cellIdx) {
  if (!bucket || !Array.isArray(bucket.cells)) return bucket;
  if (cellIdx < 0 || cellIdx >= BUCKET_WIDTH) return bucket;
  const cell = bucket.cells[cellIdx];
  if (!cell) return bucket;

  if (cell.locked) {
    const cells = bucket.cells.slice();
    cells[cellIdx] = { ...cell, locked: false };
    return { ...bucket, cells };
  }

  // promoteInBucket refuses index 0 and already clears `gone` on what it
  // promotes, so cell 0 is the locked cell in both branches.
  const promoted = cellIdx === 0 ? bucket : promoteInBucket(bucket, cellIdx);
  const cells = promoted.cells.slice();
  cells[0] = { ...cells[0], locked: true, gone: false };
  return { ...promoted, cells };
}

// ── Per-slot budget ─────────────────────────────────────────────────────────
//
// A bucket can EARMARK money before anyone is picked for it: "RB1 gets $60"
// while the primary listed there costs $51. So a plan reports two different
// totals — `planned` (what you have set aside) and `targeted` (what the names
// you actually listed would cost) — and they are allowed to disagree. That gap
// is the useful signal: it is the room you left yourself at that slot.

/**
 * What this slot costs the plan.
 *
 * 🔴 The default to the primary's price is what makes this backwards
 * compatible. A plan where nobody has touched a budget behaves EXACTLY as it
 * did before per-slot budgets existed, so `planned === targeted` throughout and
 * every pre-existing pin stays true without being rewritten. `budget: null` is
 * therefore a real value meaning "follow the primary", NOT missing data.
 */
export function slotBudget(bucket) {
  if (!bucket) return 0;
  if (bucket.budget != null) return Math.max(0, Number(bucket.budget) || 0);
  const primary = primaryOf(bucket);
  return primary ? (Number(primary.price) || 0) : 0;
}

/**
 * Has the user set ANY rail budget by hand on this plan?
 *
 * 🔴 `budget: null` IS A VALUE, NOT MISSING DATA — see slotBudget. So this asks
 * whether an explicit number exists anywhere, which is exactly the question
 * "may I seed every rail without overwriting something the user typed".
 */
export function hasExplicitSlotBudget(plan) {
  return Object.values((plan && plan.buckets) || {}).some(
    (bucket) => bucket && bucket.budget != null
  );
}

/**
 * Does this slot count as spoken for?
 *
 * 🔴 A slot with a budget and NO player is COMMITTED. Its money counts toward
 * `planned`, and — the part worth deciding deliberately — it stops demanding
 * its own $1 out of max bid. That is the entire point of earmarking: once you
 * have said "RB1 gets $60", max bid must stop pretending you still need a
 * dollar in hand to fill RB1, because the $60 already covers it. Treating an
 * earmarked-but-empty slot as empty would double-count it, once in `planned`
 * and again in the max-bid holdback.
 */
export function isCommitted(bucket) {
  if (!bucket) return false;
  return bucket.budget != null || primaryOf(bucket) != null;
}

// ── Input normalization ─────────────────────────────────────────────────────
//
// 🔴 THE ONLY PLACE IN THIS FILE ALLOWED TO BE DEFENSIVE ABOUT SHAPE.
//
// Plans enter through exactly two doors — the localStorage loader and
// importPlans — and BOTH carry user-editable JSON. Export is a shipped button,
// so a plan file is something a user can open, hand-edit, and re-import; and
// localStorage is one devtools tab away. "Well-formed" is therefore an
// assumption we BUY here, not one we get for free anywhere else.
//
// What goes wrong without it, all three confirmed:
//   • `allocated += primary.price` with price:"20" string-concats to "020";
//     with price:undefined it yields NaN, which propagates through remaining
//     and maxBid so every budget tile renders $NaN — silently, no throw.
//   • A plan with no `buckets` key crashes updateBucket on the first edit
//     ("Cannot read properties of undefined"). planTotals guards it; the write
//     path does not, and is not going to.
//   • A cells array that is not exactly BUCKET_WIDTH long shuffles the row:
//     promoteInBucket(bucket, 3) on a 3-wide array writes the old primary into
//     cell 3, reads the now-undefined cell 3 back as the promoted value, and
//     drops null into cell 0 — losing the primary with no error.
//
// Deliberately NOT applied on write paths. Those already produce correct shapes
// by construction, and running this per keystroke would allocate a whole new
// plan object per character typed into a price field.

/** A Target, or null. No key ⇒ no target: the key is what dynamicPrices reads. */
export function normalizeTarget(t) {
  if (!t || !t.key) return null;
  return {
    key: String(t.key),
    name: String(t.name ?? ""),
    pos: String(t.pos ?? ""),
    team: t.team ?? null,
    market: Math.max(0, Number(t.market) || 0),
    price: Math.max(0, Number(t.price) || 0),
    edited: t.edited === true,
    // 🔴 An old stored plan has no `locked` field, and `=== true` is what makes
    // it load as unlocked rather than as undefined — which would then flow into
    // the solver's held set as a falsy-but-present value and read fine right up
    // until somebody wrote `'locked' in target`.
    gone: t.gone === true && t.locked !== true,
    locked: t.locked === true,
  };
}

/** Always exactly BUCKET_WIDTH cells — short rows pad, long rows truncate. */
export function normalizeBucket(b) {
  const raw = (b && Array.isArray(b.cells)) ? b.cells : [];
  return {
    // 🔴 null MUST survive as null. Coercing it to 0 would earmark every slot at
    // nothing, which reads as "committed for $0" — killing the
    // default-to-primary-price path and silently zeroing every plan on load.
    budget: b && b.budget != null ? Math.max(0, Number(b.budget) || 0) : null,
    cells: Array.from({ length: BUCKET_WIDTH }, (_, i) => normalizeTarget(raw[i])),
  };
}

/** A Plan with every field present and every bucket the right width. */
export function normalizePlan(p, fallbackBudget) {
  const src = p || {};
  const rawBuckets = (src.buckets && typeof src.buckets === "object") ? src.buckets : {};
  const buckets = {};
  Object.entries(rawBuckets).forEach(([slotId, b]) => { buckets[slotId] = normalizeBucket(b); });
  return {
    id: src.id || newPlanId(),
    name: src.name || "Imported Plan",
    budget: Math.max(0, Number(src.budget) || fallbackBudget || 0),
    buckets,
    // Optimizer provenance, when the plan came from one. Carried through the
    // normalizer so the "differs" markers and the board-price caveat survive a
    // reload — without this they would silently vanish on refresh and the plan
    // would start reading as a promise instead of an estimate.
    // Every plan stored before the toolbar split loads as PPG, which is what
    // those plans were actually solved under.
    objective: planObjective(src),
    budgetMode: planBudgetMode(src),
    // Absent on every plan stored before the flag existed — but NOT therefore
    // unclaimed: rename shipped long before the flag did. See inferUserNamed.
    userNamed: inferUserNamed(src),
    optimized: src.optimized && typeof src.optimized === "object" ? {
      mode: String(src.optimized.mode || ""),
      objective: String(src.optimized.objective || ""),
      differs: Array.isArray(src.optimized.differs) ? src.optimized.differs.map(String) : [],
      // Which slots the run HELD rather than solved. Survives a reload for the
      // same reason `differs` does: without it a reopened plan cannot say what
      // its result actually kept.
      lockedSlots: Array.isArray(src.optimized.lockedSlots) ? src.optimized.lockedSlots.map(String) : [],
      // 🔴 null, NOT "", for a plan generated before fingerprints existed. The
      // three states are "no fingerprint / matches / differs" and only the last
      // may show a warning: an empty string would compare unequal to every real
      // hash and put "prices changed" on a plan nobody can say that about.
      fingerprint: src.optimized.fingerprint ? String(src.optimized.fingerprint) : null,
      note: src.optimized.note ? String(src.optimized.note) : null,
    } : null,
  };
}

/**
 * All plan budget math in one place, derived from primaryOf outward.
 *
 * 🔴 IT READS THE PLAN'S BUDGET MODE, because "is this spot spoken for" has
 * two different honest answers and the mode is what picks between them.
 *
 *   TOTAL - an earmark IS a commitment. You said RB1 gets $60 out of one pot,
 *           so the spot is settled and only the name is missing.
 *   SLOT  - an earmark is a CAP on a spot that is still open. The money is set
 *           aside (PLANNED counts it either way) but the spot has nobody in it,
 *           it still needs a player, and the most you can bid is bounded by
 *           what every OTHER open spot is holding.
 *
 * So `committed` keeps the earmark sense in both modes and is what "N of 16
 * spots committed" reads; `emptySlots` is the OPEN count, which under PER SPOT
 * means "has no name". That split is why a plan with sixteen rails and no
 * players stopped claiming its roster was full.
 *
 * Derived from the plan rather than passed in, so every caller of planTotals
 * agrees with every other one for free and no call site can forget it. A plan
 * saved before budget modes existed reads "total" and behaves exactly as it did.
 */
export function planTotals(plan, rosterSlots) {
  const buckets = (plan && plan.buckets) || {};
  const budget = Number(plan && plan.budget) || 0;
  const bySlot = planBudgetMode(plan) === "slot";

  let planned = 0;    // Σ slotBudget — what you have set aside
  let targeted = 0;   // Σ primary price — what the listed names would cost
  let secured = 0;    // Σ LOCKED primary price — of that, what you already own
  let filled = 0;     // slots with a primary
  let committed = 0;  // slots with a primary OR an earmark
  let lockedSlots = 0;
  let unplanned = 0;  // slots with neither a name nor an earmark
  const spendingByPos = {};

  // What every slot with no name is holding back: its own earmark where it has
  // one, $1 where it does not. Only read in slot mode, where an earmarked empty
  // slot is still open; see maxBid.
  let openReserve = 0;
  let openCount = 0;
  let biggestOpenReserve = 0;
  let namedSpend = 0;  // Σ earmark over the slots that DO have a name

  // 🔴 The starters/bench split lives HERE because it is purely structural —
  // slot.pos === "BN" and nothing else — which is exactly the kind of thing
  // planTotals already reasons about. Both groups are ALWAYS present and always
  // zeroed rather than absent, so a caller can read byGroup.bench.planned on a
  // roster with no bench without guarding. PPG is deliberately NOT here: it
  // needs getPlayerProj, and this function stays pure over (plan, rosterSlots)
  // with no pricing dependency.
  const byGroup = {
    starters: { spots: 0, committed: 0, planned: 0, targeted: 0 },
    bench: { spots: 0, committed: 0, planned: 0, targeted: 0 },
  };

  rosterSlots.forEach(slot => {
    const group = slot.pos === "BN" ? byGroup.bench : byGroup.starters;
    group.spots += 1;

    const bucket = buckets[slot.id];
    const primaryHere = primaryOf(bucket);
    if (!primaryHere) {
      openCount += 1;
      const reserve = bucket && bucket.budget != null
        ? Math.max(0, Number(slotBudget(bucket)) || 0)
        : 1;
      openReserve += reserve;
      if (reserve > biggestOpenReserve) biggestOpenReserve = reserve;
    }

    // An earmarked-but-empty slot is spoken for; an untouched one is not.
    if (!isCommitted(bucket)) { unplanned += 1; return; }
    committed += 1;
    group.committed += 1;

    const primary = primaryHere;
    // Belt and braces: planTotals is exported, so a component can hand it a
    // plan that never went through normalizePlan. A single un-coerced number
    // turns planned, remaining AND maxBid into NaN with no error anywhere.
    const earmark = Number(slotBudget(bucket)) || 0;
    planned += earmark;
    group.planned += earmark;
    if (primary) {
      filled += 1;
      namedSpend += earmark;
      const price = Number(primary.price) || 0;
      targeted += price;
      group.targeted += price;
      // 🔴 A PARTITION OF `targeted`, NOT A NEW SUM. `secured` is the part of
      // the same total that is already yours, so secured + targetedOpen ===
      // targeted === allocated by construction and no tile can drift from
      // another. Adding a locked player's price a second time here — the
      // obvious mistake — would double-count every purchase.
      if (primary.locked) {
        secured += price;
        lockedSlots += 1;
      }
    }

    // Same branch shape as useBuilderState spendingByPos: FLEX/SFLEX/BN bucket
    // under the SLOT pos, everything else under the PLAYER pos — except that an
    // earmarked slot with nobody in it has no player pos to read, so it falls
    // back to its own. Amounts are slotBudget, so the legend adds up to
    // `planned` rather than to `targeted`.
    const primaryPos = primary ? primary.pos : null;
    const slotPos = slot.pos || primaryPos;
    const pos = (slotPos === "BN" || slotPos === "FLEX" || slotPos === "SFLEX")
      ? slotPos
      : (primaryPos || slotPos);
    if (pos) {
      if (!spendingByPos[pos]) spendingByPos[pos] = 0;
      spendingByPos[pos] += earmark;
    }
  });

  // 🔴 OPEN, NOT UNCOMMITTED. Under PER SPOT a rail is a cap on a spot that
  // still needs a player, so the open count is the count of slots with no name;
  // under TOTAL an earmark settles the spot and only `unplanned` is open.
  const emptySlots = bySlot ? openCount : rosterSlots.length - committed;
  const remaining = budget - planned;

  // Mirrors useBuilderState.js maxBid (the `emptySlots > 1 ? ... ` expression in
  // its Computed block) with two deliberate differences: clamped at 0, and 0 when
  // the roster is full. A plan can legally be over budget while you shop; a
  // built roster cannot. planBudget.test.js pins both to the same value on the
  // in-range cases, so this divergence stays intentional and does not drift.
  //
  // 🔴 PER SPOT PAYS THE RAIL, NOT A DOLLAR. Every other open spot holds back
  // what it is actually budgeted for, so the biggest bid you could make is the
  // budget less every other spot's holding, which is the same thing as freeing
  // the largest rail you have. With no rails set at all `openReserve` is $1 a
  // spot and this collapses to the TOTAL expression exactly, which is what
  // makes the two modes agree on a plan that never used the feature.
  const maxBid = emptySlots === 0
    ? 0
    : bySlot
      ? Math.max(0, budget - namedSpend - (openReserve - biggestOpenReserve))
      : Math.max(0, remaining - (emptySlots - 1));

  // `allocated` is an alias of `targeted`, kept so every existing call site and
  // pin reads the same number it always did. With no per-slot budgets set,
  // planned === targeted === allocated and nothing above this line behaves
  // differently from the pre-budget version.
  return {
    planned, targeted, allocated: targeted,
    // The two halves of `allocated`: what you have already bought, and what the
    // names you are still shopping for would cost.
    secured, targetedOpen: targeted - secured, lockedSlots,
    filled, committed,
    // Slots with neither a name nor an earmark: the ones that still need money
    // as well as a player. Equal to `emptySlots` in total mode by construction.
    unplanned,
    emptySlots, remaining, maxBid, spendingByPos,
    byGroup,
  };
}

/**
 * 🔴 THE IDENTITY AN OPTIMIZER RE-RUN REPLACES ON: mode AND objective.
 *
 * Three optimizer modes exist now, so mode alone is not enough — "Optimize
 * remaining" replacing its own output must never clobber "Re-solve at my
 * prices", and PPG must not clobber VORP.
 *
 * 🔴 AND IT IS KEYED ON THE FLAG, NEVER THE NAME. A hand-built plan the user
 * happened to call "Optimized (PPG)" has no `optimized` object, so it can
 * never match, never be replaced, and never be swept by "Delete all
 * optimized". Matching on the name would quietly destroy their work.
 */
export const generatedKey = (plan) => (plan && plan.optimized && plan.optimized.mode
  ? `${plan.optimized.mode}|${plan.optimized.objective}`
  : null);

/**
 * Was this plan produced by the optimizer AND still unclaimed?
 *
 * 🔴 A RENAME TAKES A PLAN OUT OF THIS SET, and that is a safety property
 * rather than a nicety. Both the picker's "Optimized" group and the
 * delete-all-optimized sweep read this one predicate, so before `userNamed`
 * existed, renaming "Plan B (VORP, rebuilt)" to "DRAFT DAY" left it classified
 * as disposable — and the sweep, which is how twenty test plans get cleared, was
 * one click from taking it. On draft morning that is the worst thing this
 * feature can do, and it was reachable by accident.
 *
 * A user who names a thing has claimed it. The provenance is NOT erased —
 * `optimized` keeps the mode, the objective, the note and the fingerprint, so
 * the plan still reports staleness and still says what built it. It simply
 * stops being something the bulk delete may take.
 *
 * 🔴 STILL THE FLAG, NEVER THE NAME. The rule did not become "does the name
 * look custom" — that would misfile a generated plan whose auto-name someone
 * liked, and it was the wrong test before for the same reason. `userNamed` is
 * set by one function (renamePlan) and means one thing: a person typed this.
 */
export const isGenerated = (plan) => generatedKey(plan) !== null && !(plan && plan.userNamed);

/** Has a person named this plan? Read by the picker, the sweep and the namer. */
export const isUserNamed = (plan) => !!(plan && plan.userNamed);

/**
 * Every name this app has ever generated for a plan, as one pattern.
 *
 * Two eras, both still in storage:
 *   "Plan C (PPG, rebuilt)"  — objective + scope, current
 *   "Optimized (PPG) at my prices" — and its siblings, before the toolbar split
 *
 * The letter part follows nextPlanName, which runs A-Z, then "Plan 27" upward,
 * then "New Plan" as its floor — so all three shapes are here.
 */
const GENERATED_NAME_RE = new RegExp([
  "^(?:Plan (?:[A-Z]|[0-9]+)|New Plan) \\((?:PPG|VORP), (?:filled|rebuilt|kept)\\)$",
  "^Optimized(?: \\((?:PPG|VORP)\\))?(?: at my prices| around commitments)?$",
].join("|"));

/**
 * 🔴 THE MIGRATION FOR PLANS RENAMED BEFORE `userNamed` EXISTED.
 *
 * Rename has been in the ⋯ menu since 8865bc3, so plans in storage right now
 * were renamed by hand and carry no flag. They load as generated, and the sweep
 * still eats them — which is the exact hole the flag was added to close,
 * surviving in the one population that matters: the plans a person already has,
 * one of which may be the real draft-day plan.
 *
 * The inference is sound in one direction only, so it is only made in that
 * direction: a plan carrying `optimized.mode` and called "DRAFT DAY" cannot have
 * got that name from this code, so a human typed it.
 *
 * 🔴 AND THE ERROR IS RESOLVED PROTECTIVELY, ON PURPOSE. Anything that does not
 * match a name this app generates is treated as claimed. A false positive
 * leaves one extra plan in the picker that a sweep would have cleared — a
 * nuisance, fixed by deleting it. A false negative deletes somebody's work.
 * That asymmetry decides every ambiguous case here, including a duplicate
 * ("Plan B (PPG, filled) copy"), which reads as claimed and is left alone.
 *
 * Plans with no `optimized` block are untouched: they were never in the
 * generated set, so nothing can sweep them and there is nothing to protect
 * them from.
 */
export function inferUserNamed(src) {
  if (!src || typeof src !== "object") return false;
  if (src.userNamed === true) return true;
  const mode = src.optimized && typeof src.optimized === "object" ? src.optimized.mode : null;
  if (!mode) return false;
  const name = String(src.name || "").trim();
  if (!name) return false;
  return !GENERATED_NAME_RE.test(name);
}

// ── The price sheet ─────────────────────────────────────────────────────────
//
// A per-league map of playerKey → the price YOU say that player costs, for
// ANY player — not only one already sitting in a slot. Without it "what if I
// get CMC at $30" is unanswerable until you first place CMC somewhere.
//
// 🔴 IT DOES NOT WRITE TO dynamicPrices, AND MUST NOT. usePricing still owns
// the board: the bid AI, the mock draft, PlayerCard and Team Builder all keep
// reading one unchanged number, and `dynamicPrices` remains the single
// rendered user-facing price everywhere outside this feature. This is a
// PLANNING OVERLAY, read only by Draft Plan and its solver.
//
// That distinction is the whole reason this is safe, and it is exactly what
// made the removed customValues feature a problem — a user price that leaked
// into the board changed what the AI bid and what every other surface showed.
// If someone proposes "unifying" the sheet with dynamicPrices, this paragraph
// is the argument against it.

/** Plans have no league of their own; this is the sheet's key for one. */
export const sheetLeagueKey = (activeLeagueName) => activeLeagueName || "__default__";

/**
 * Lift every hand-edited target price out of the plans and into a sheet.
 *
 * One-time migration for plans written before the sheet existed, where a
 * price could only live on a target. Idempotent by construction: a key already
 * in the sheet is NEVER clobbered, so a second run leaves everything alone.
 * Two plans disagreeing about the same player is the only conflict possible,
 * and there the highest wins — it is the one that says "I would pay this".
 */
export function liftEditedPrices(plans, existing = {}) {
  const out = { ...existing };
  const preexisting = new Set(Object.keys(existing));
  (plans || []).forEach((plan) => {
    Object.values((plan && plan.buckets) || {}).forEach((bucket) => {
      ((bucket && bucket.cells) || []).forEach((target) => {
        if (!target || !target.edited || !target.key) return;
        if (preexisting.has(target.key)) return;   // the sheet already spoke
        const price = Math.max(0, Number(target.price) || 0);
        out[target.key] = out[target.key] === undefined ? price : Math.max(out[target.key], price);
      });
    });
  });
  return out;
}

/**
 * Materialise the sheet onto every target in every plan.
 *
 * target.price stays the stored, summed value — planTotals keeps reading it,
 * so the tested budget core never learns the sheet exists — but it becomes a
 * COPY of the sheet rather than an independent number. One price, two places
 * to type it.
 *
 * A key absent from the sheet hands the target back to the board: edited goes
 * false and the price returns to `market`, which is what makes "clear my
 * prices" fall back rather than freeze the last value.
 *
 * 🔴 Returns the SAME array when nothing changed. The sync runs from an effect
 * keyed on the sheet, so identity is what stops it re-rendering forever.
 */
export function applySheetToPlans(plans, sheet) {
  let planChanged = false;
  const next = (plans || []).map((plan) => {
    let bucketsChanged = false;
    const buckets = {};
    Object.entries((plan && plan.buckets) || {}).forEach(([slotId, bucket]) => {
      let cellsChanged = false;
      const cells = ((bucket && bucket.cells) || []).map((target) => {
        if (!target) return target;
        // 🔴 A LOCKED PRICE IS WHAT HE COST, and the sheet does not get to
        // restate it. Without this, setting your price for a player you have
        // already bought — or clearing the sheet, which resets un-edited
        // targets to `market` — silently rewrites a real auction result while
        // the draft is still running. Same guard as `edited`, one clause up,
        // because the reason is the same: a number a person put there.
        if (target.locked) return target;
        const own = sheet[target.key];
        if (own !== undefined) {
          if (target.edited && target.price === own) return target;
          cellsChanged = true;
          return { ...target, price: own, edited: true };
        }
        if (!target.edited) return target;
        cellsChanged = true;
        return { ...target, price: target.market, edited: false };
      });
      buckets[slotId] = cellsChanged ? { ...bucket, cells } : bucket;
      if (cellsChanged) bucketsChanged = true;
    });
    if (!bucketsChanged) return plan;
    planChanged = true;
    return { ...plan, buckets };
  });
  return planChanged ? next : plans;
}

// A stable identity for "this league has set no prices", so the sync effect
// below is not re-triggered by a fresh {} on every render.
const EMPTY_SHEET = {};

/**
 * The name a new plan gets: the first UNUSED letter, then numbers.
 *
 * 🔴 IT WAS `String.fromCharCode(65 + plans.length)`, WHICH COLLIDES. Delete
 * Plan B out of A/B/C and the next new plan is "Plan C" — a second live plan
 * with a name the picker already shows, which is unfixable without a rename and
 * makes the two indistinguishable in the dropdown. Counting plans answers "how
 * many have I got", and the question is "which letters are free".
 *
 * 🔴 AND IT RAN OFF THE END OF THE ALPHABET. At 26 plans it emitted "Plan [",
 * then "Plan \", "Plan ]" — punctuation, from walking past Z in the character
 * table. Past Z this returns "Plan 27" and counts on.
 *
 * Matches on the NAME, because the name is what collides. A renamed plan
 * therefore frees its old letter, which is right: the letter is only reserved
 * while something visible is using it.
 */
export function nextPlanName(plans) {
  const taken = new Set((plans || []).map((p) => p && p.name));
  for (let i = 0; i < 26; i++) {
    const name = `Plan ${String.fromCharCode(65 + i)}`;
    if (!taken.has(name)) return name;
  }
  // Bounded, not `while (true)`: there are at most `taken.size` names, so one
  // of these must be free — but the bound means a future edit cannot turn this
  // into a hang.
  for (let n = 27; n <= taken.size + 27; n++) {
    const name = `Plan ${n}`;
    if (!taken.has(name)) return name;
  }
  return "New Plan";
}

const seedPlan = (budget, name = "Plan A") => ({
  id: newPlanId(),
  name,
  budget,
  objective: DEFAULT_OBJECTIVE,
  budgetMode: DEFAULT_BUDGET_MODE,
  buckets: {},
  optimized: null,
});

// activeLeagueName is FOURTH, matching useBuilderState's shape.
export default function usePlanState(settings, rosterSlots, dynamicPrices, activeLeagueName = null) {
  const BUDGET = settings.budget;

  // ── State ─────────────────────────────────────────────────────────────────
  // Same try/catch-JSON.parse shape useBuilderState uses for savedBuilds, with
  // a seeded single plan rather than an empty list: a planner with no plan has
  // nothing to render, so "absent" and "one empty Plan A" are the same state.
  const [plans, setPlans] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PLANS_KEY));
      // Entry point 1 of 2. Whatever is in localStorage is user-editable —
      // normalize before it can reach planTotals or a write path.
      if (Array.isArray(saved) && saved.length > 0) return saved.map(p => normalizePlan(p, BUDGET));
    } catch { /* ignore */ }
    return [seedPlan(BUDGET)];
  });

  const [activePlanId, setActivePlanId] = useState(() => {
    try { return localStorage.getItem(ACTIVE_PLAN_KEY) || null; }
    catch { return null; }
  });

  // The price sheet, whole — every league. Same try/catch-JSON.parse shape as
  // the plans above.
  const [priceSheet, setPriceSheet] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PRICE_SHEET_KEY));
      if (saved && typeof saved === "object") return saved;
    } catch { /* ignore */ }
    return {};
  });

  // 🔴 A SEPARATE SHEET, NOT A SECOND FIELD ON THE PRICE ONE. A price is
  // MATERIALISED — copied onto every matching target and synced across plans,
  // because planTotals sums target.price. A PPG override is purely DERIVED:
  // nothing stores it, ppgFor just reads it at render. One object holding both
  // would imply a symmetry that does not exist and would drag the PPG override
  // through a sync path it has no business in.
  const [ppgSheet, setPpgSheet] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PPG_SHEET_KEY));
      if (saved && typeof saved === "object") return saved;
    } catch { /* ignore */ }
    return {};
  });

  // 🔴 A THIRD SHEET, AND IT IS NOT A FIELD ON EITHER OF THE OTHER TWO.
  // Those hold NUMBERS the solver reads — a price is materialised onto every
  // matching target, a PPG override is read at render by ppgFor. A star is a
  // FLAG the solver never sees: it changes what the list shows you and how a
  // card is painted, and nothing else. Folding it into the price sheet would
  // put a boolean into a map every price path does `Number()` on, and would
  // make "clear my prices" unstar everyone — which is why the clear control
  // for this one is separate too.
  //
  // A star is a property of the PLAYER, not of a cell: it follows him into
  // every plan and every column, which is what makes one league-scoped sheet
  // the right shape rather than a flag on Target.
  const [starSheet, setStarSheet] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STAR_SHEET_KEY));
      if (saved && typeof saved === "object") return saved;
    } catch { /* ignore */ }
    return {};
  });

  // Which cell the player panel is filling.
  const [activeCell, setActiveCell] = useState(null); // { slotId, cellIdx } | null

  // ── Persistence (each hook owns its own) ──────────────────────────────────
  useEffect(() => {
    try { localStorage.setItem(PLANS_KEY, JSON.stringify(plans)); }
    catch { /* ignore */ }
  }, [plans]);

  useEffect(() => {
    try { localStorage.setItem(PRICE_SHEET_KEY, JSON.stringify(priceSheet)); }
    catch { /* ignore */ }
  }, [priceSheet]);

  useEffect(() => {
    try { localStorage.setItem(PPG_SHEET_KEY, JSON.stringify(ppgSheet)); }
    catch { /* ignore */ }
  }, [ppgSheet]);

  useEffect(() => {
    try { localStorage.setItem(STAR_SHEET_KEY, JSON.stringify(starSheet)); }
    catch { /* ignore */ }
  }, [starSheet]);

  useEffect(() => {
    try {
      if (activePlanId) localStorage.setItem(ACTIVE_PLAN_KEY, activePlanId);
      else localStorage.removeItem(ACTIVE_PLAN_KEY);
    } catch { /* ignore */ }
  }, [activePlanId]);

  // ── Computed ──────────────────────────────────────────────────────────────
  // A stale activePlanId (plan deleted in another tab, id dropped on import)
  // falls back to the first plan rather than rendering nothing.
  const activePlan = useMemo(
    () => plans.find(p => p.id === activePlanId) || plans[0] || null,
    [plans, activePlanId]
  );

  const buckets = useMemo(() => (activePlan && activePlan.buckets) || {}, [activePlan]);

  // ── The price sheet, for THIS league ──────────────────────────────────────
  const leagueKey = sheetLeagueKey(activeLeagueName);
  const leaguePrices = useMemo(() => priceSheet[leagueKey] || EMPTY_SHEET, [priceSheet, leagueKey]);

  /**
   * Set — or with a null/blank price, clear — YOUR price for a player,
   * league-wide and whether or not they are in any plan.
   */
  const setSheetPrice = useCallback((key, price) => {
    if (!key) return;
    setPriceSheet((prev) => {
      const league = { ...(prev[leagueKey] || {}) };
      if (price === null || price === undefined || price === "") {
        if (!(key in league)) return prev;
        delete league[key];
      } else {
        const n = Math.max(0, Number(price) || 0);
        if (league[key] === n) return prev;
        league[key] = n;
      }
      return { ...prev, [leagueKey]: league };
    });
  }, [leagueKey]);

  const leaguePpg = useMemo(() => ppgSheet[leagueKey] || EMPTY_SHEET, [ppgSheet, leagueKey]);

  /**
   * Set — or with a blank value, clear — YOUR projected points per game for a
   * player. "CMC does 21 again."
   *
   * 🔴 IT NEVER TOUCHES THE PROJECTION ENGINE. getPlayerProj, projectPoints and
   * usePricing's customStats are all untouched, so the mock draft AI, Team
   * Builder and PlayerCard keep reading unmodified projections. customStats is
   * inert today and looks like the natural home for this — it is not, it feeds
   * the engine. Same overlay rule as the price sheet, for the same reason.
   */
  const setSheetPpg = useCallback((key, ppg) => {
    if (!key) return;
    setPpgSheet((prev) => {
      const league = { ...(prev[leagueKey] || {}) };
      if (ppg === null || ppg === undefined || ppg === "") {
        if (!(key in league)) return prev;
        delete league[key];
      } else {
        const n = Math.max(0, Number(ppg) || 0);
        if (league[key] === n) return prev;
        league[key] = n;
      }
      return { ...prev, [leagueKey]: league };
    });
  }, [leagueKey]);

  /** Drop every PPG override for this league. */
  const clearSheetPpg = useCallback(() => {
    setPpgSheet((prev) => {
      if (!prev[leagueKey] || Object.keys(prev[leagueKey]).length === 0) return prev;
      const next = { ...prev };
      delete next[leagueKey];
      return next;
    });
  }, [leagueKey]);

  const leagueStars = useMemo(() => starSheet[leagueKey] || EMPTY_SHEET, [starSheet, leagueKey]);

  /**
   * Star or unstar a player, league-wide. "My guys" — the names you want to
   * see wherever they turn up, whether or not they are in a plan.
   *
   * 🔴 An unstar DELETES the key rather than writing false. Object.keys() is
   * the count the STARRED pill shows and the clear control gates on, so a
   * lingering `false` would make an empty sheet claim members. The sheet is
   * only ever read as "is this key present".
   */
  const toggleStar = useCallback((key, on) => {
    if (!key) return;
    setStarSheet((prev) => {
      const league = { ...(prev[leagueKey] || {}) };
      const next = on === undefined ? !league[key] : !!on;
      if (next === !!league[key]) return prev;
      if (next) league[key] = true;
      else delete league[key];
      return { ...prev, [leagueKey]: league };
    });
  }, [leagueKey]);

  /**
   * Unstar everyone in this league. Its OWN control, deliberately — clearing
   * your prices must not unstar your guys and vice versa. Two kinds of thing,
   * two undos.
   */
  const clearStars = useCallback(() => {
    setStarSheet((prev) => {
      if (!prev[leagueKey] || Object.keys(prev[leagueKey]).length === 0) return prev;
      const next = { ...prev };
      delete next[leagueKey];
      return next;
    });
  }, [leagueKey]);

  /** Drop every price you have set for this league. Other leagues untouched. */
  const clearSheetPrices = useCallback(() => {
    setPriceSheet((prev) => {
      if (!prev[leagueKey] || Object.keys(prev[leagueKey]).length === 0) return prev;
      const next = { ...prev };
      delete next[leagueKey];
      return next;
    });
  }, [leagueKey]);

  // 🔴 ONE-TIME MIGRATION, then the sheet is authoritative. Plans written
  // before the sheet existed carry prices only on their targets; lift them in
  // once. Guarded by a ref rather than a dependency list because it must run
  // exactly once per mount, not once per league change — switching leagues
  // must not drag another league's edits into this one's sheet.
  const migratedRef = useRef(false);
  useEffect(() => {
    if (migratedRef.current) return;
    migratedRef.current = true;
    setPriceSheet((prev) => {
      const league = prev[leagueKey] || {};
      const lifted = liftEditedPrices(plans, league);
      if (Object.keys(lifted).length === Object.keys(league).length) return prev;
      return { ...prev, [leagueKey]: lifted };
    });
  }, [plans, leagueKey]);

  // The sheet is the source of truth for user prices, so every target that
  // matches one is a copy of it. applySheetToPlans returns the same array when
  // nothing differs, which is what keeps this from looping.
  useEffect(() => {
    setPlans((prev) => applySheetToPlans(prev, leaguePrices));
  }, [leaguePrices]);

  const primaryFor = useMemo(() => (slotId) => primaryOf(buckets[slotId]), [buckets]);

  const totals = useMemo(() => planTotals(activePlan, rosterSlots), [activePlan, rosterSlots]);
  const {
    planned, targeted, allocated, secured, targetedOpen, lockedSlots,
    filled, committed,
    // 🔴 `unplanned` IS NOT A DUPLICATE OF `emptySlots` AND MUST BE CARRIED.
    // planTotals has computed it since PER SPOT arrived, and it was dropped
    // here and at the return below, so every consumer read `undefined`: the
    // summary's alert silently fell back to `emptySlots` (right by accident in
    // total mode, wrong in slot mode) and DraftPlan's `avgPerOpen` guard —
    // `unplanned > 0` — was false on every plan, so a fresh $200 roster
    // reported "16 spots unplanned, about $0 each".
    unplanned,
    emptySlots, remaining, maxBid, spendingByPos, byGroup,
  } = totals;

  // ── Actions ───────────────────────────────────────────────────────────────
  // Every mutation is an immutable update to the ACTIVE plan inside `plans`.
  const updateActive = useCallback((fn) => {
    setPlans(prev => {
      if (prev.length === 0) return prev;
      const found = prev.findIndex(p => p.id === activePlanId);
      const idx = found === -1 ? 0 : found;
      const next = prev.slice();
      next[idx] = fn(prev[idx]);
      return next;
    });
  }, [activePlanId]);

  const updateBucket = useCallback((slotId, fn) => {
    updateActive(plan => {
      const current = plan.buckets[slotId] || emptyBucket();
      const updated = fn(current);
      if (updated === current) return plan;
      return { ...plan, buckets: { ...plan.buckets, [slotId]: updated } };
    });
  }, [updateActive]);

  const addTarget = useCallback((slotId, cellIdx, player) => {
    if (cellIdx < 0 || cellIdx >= BUCKET_WIDTH) return;
    const target = makeTarget(player, dynamicPrices);
    // No-op if that key is already somewhere in the row — a bucket is a set of
    // alternatives, and the same name twice is never one. Computed once here so
    // the write and the activeCell advance agree on whether anything happened.
    const existing = buckets[slotId] || emptyBucket();
    if (existing.cells.some(c => c && c.key === target.key)) return;

    updateBucket(slotId, (bucket) => {
      if (bucket.cells.some(c => c && c.key === target.key)) return bucket;
      const cells = bucket.cells.slice();
      cells[cellIdx] = target;
      return { ...bucket, cells };
    });

    // Advance the panel to the next empty cell in the same row, or clear it.
    const after = existing.cells.slice();
    after[cellIdx] = target;
    const nextEmpty = after.findIndex(c => !c);
    setActiveCell(nextEmpty === -1 ? null : { slotId, cellIdx: nextEmpty });
  }, [dynamicPrices, updateBucket, buckets]);

  const removeTarget = useCallback((slotId, cellIdx) => {
    updateBucket(slotId, (bucket) => {
      if (!bucket.cells[cellIdx]) return bucket;
      const cells = bucket.cells.slice();
      cells[cellIdx] = null;
      return { ...bucket, cells };
    });
  }, [updateBucket]);

  // The entire promote implementation. There is no primary pointer to keep in
  // sync — cell 0 IS the primary, and primaryOf reads it.
  const promoteTarget = useCallback((slotId, cellIdx) => {
    updateBucket(slotId, (bucket) => promoteInBucket(bucket, cellIdx));
  }, [updateBucket]);

  const toggleGone = useCallback((slotId, cellIdx) => {
    updateBucket(slotId, (bucket) => toggleGoneInBucket(bucket, cellIdx));
  }, [updateBucket]);

  /**
   * "I won him." Locking a backup promotes it first — see toggleLockInBucket.
   *
   * Takes a cellIdx like every other cell action, so the toolbar of a card can
   * hand it the same argument its neighbours get.
   */
  const toggleLock = useCallback((slotId, cellIdx) => {
    updateBucket(slotId, (bucket) => toggleLockInBucket(bucket, cellIdx));
  }, [updateBucket]);

  /**
   * Editing a price on a card writes THROUGH to the sheet, which then syncs
   * back onto this target and every other copy of that player in every plan.
   *
   * 🔴 Because the sheet is league-wide, repricing Dak on one plan reprices him
   * on all of them. That is what "per league" means and it is the point — one
   * number, two places to type it — but it is worth knowing before it surprises
   * someone comparing two plans.
   */
  const setTargetPrice = useCallback((slotId, cellIdx, price) => {
    const bucket = buckets[slotId];
    const target = bucket && bucket.cells ? bucket.cells[cellIdx] : null;
    if (!target) return;
    setSheetPrice(target.key, price);
  }, [buckets, setSheetPrice]);

  /**
   * Earmark money for a slot. `null` clears the earmark and hands the slot back
   * to the default — slotBudget then follows the primary's price again, which
   * is the pre-budget behaviour. Note that clearing is NOT the same as setting
   * 0: 0 is a deliberate "this slot gets nothing and is still spoken for".
   */
  const setSlotBudget = useCallback((slotId, n) => {
    updateBucket(slotId, (bucket) => ({
      ...bucket,
      budget: n == null ? null : Math.max(0, Number(n) || 0),
    }));
  }, [updateBucket]);

  /**
   * Set every slot's rail budget to what that slot currently costs the plan.
   *
   * 🔴 ONE update, not sixteen. The whole fill has to land as a single change
   * so the staleness fingerprint moves once and the Rebuild prompt appears
   * once, rather than the plan flickering through fifteen intermediate states
   * that were each briefly stale for a different reason.
   *
   * 🔴 A SLOT WITH NO PRIMARY IS LEFT BLANK, deliberately. slotBudget returns 0
   * for an empty bucket, and writing that 0 would silently earmark nothing to
   * every unfilled spot, which under budgetMode "slot" caps them at $0 and
   * makes the plan unsolvable. Blank means "bound only by the total", which is
   * the honest default for a spot with nobody in it.
   */
  const fillSlotBudgetsFromTargets = useCallback((baselinePriceOf) => {
    updateActive(plan => {
      const next = { ...(plan.buckets || {}) };
      let changed = false;

      // 🔴 AN EMPTY SPOT SEEDS FROM THE ENGINE, NOT FROM A GUESS, and there are
      // two engines to seed from. A bench spot takes $1, the solver's own
      // per-bench reserve: optimizePlan holds that back for every open bench
      // slot before it solves the starters, so writing it down records a
      // constraint that was already being applied and stops the bench quietly
      // eating starter money. A STARTER takes what the optimizer paid for that
      // slot solving an empty plan on the whole budget - the baseline - which
      // is the same question the seeded plan then asks the solver, so seeding
      // every rail from it hands the solver a plan it can satisfy rather than
      // sixteen caps it has to be lucky to meet.
      //
      // 🔴 THE CALLER SUPPLIES THE BASELINE, and that is a dependency direction
      // rather than an oversight: planOptimizer imports primaryOf from this
      // file, so this file cannot import the optimizer back. Called with
      // nothing, empty starters stay blank exactly as they did.
      //
      // Walks rosterSlots rather than the buckets, because an untouched slot
      // has no bucket at all and would otherwise never be reached.
      (rosterSlots || []).forEach((slot) => {
        const bucket = next[slot.id];
        if (primaryOf(bucket)) return;             // priced below, like any slot
        const seed = slot.pos === "BN"
          ? BENCH_RESERVE
          : (baselinePriceOf ? baselinePriceOf(slot.id) : null);
        if (seed == null) return;
        const rounded = Math.max(0, Math.round(Number(seed) || 0));
        if (bucket && bucket.budget === rounded) return;
        next[slot.id] = { ...(bucket || emptyBucket()), budget: rounded };
        changed = true;
      });

      Object.entries(plan.buckets || {}).forEach(([slotId, bucket]) => {
        const primary = primaryOf(bucket);
        if (!primary) { return; }
        // 🔴 THE FALLBACK PATH, DELIBERATELY. slotBudget returns an explicit
        // budget when there is one, so calling it on the bucket as-is would
        // read back the rail we are meant to be replacing and this would
        // silently no-op on every slot the user had already set. Nulling the
        // budget first is what asks it for the primary's price.
        const seeded = Math.max(0, Math.round(slotBudget({ ...bucket, budget: null })));
        if (bucket.budget === seeded) { return; }
        next[slotId] = { ...bucket, budget: seeded };
        changed = true;
      });
      return changed ? { ...plan, buckets: next } : plan;
    });
  }, [updateActive, rosterSlots]);

  /**
   * Re-read dynamicPrices for every target in the active plan: always refresh
   * `market`, refresh `price` only when !edited.
   *
   * This is what makes the Position Dials work here for free — the dials already
   * reshape dynamicPrices upstream, so a dial move re-seeds every untouched
   * target and leaves every hand-set price alone. Scoped to the ACTIVE plan like
   * every other action; the user only ever looks at one.
   */
  const resyncMarket = useCallback(() => {
    updateActive(plan => {
      const resynced = {};
      Object.entries(plan.buckets).forEach(([slotId, bucket]) => {
        resynced[slotId] = {
          ...bucket,
          cells: bucket.cells.map(c => {
            if (!c) return null;
            const market = (dynamicPrices && dynamicPrices.get(c.key)) ?? 1;
            // `market` still refreshes on a locked target — the board delta
            // beside it stays honest — but the PRICE is what you paid. See the
            // Locks section: this and applySheetToPlans are the two writers a
            // lock has to survive, and this one fires on every dial move.
            return { ...c, market, price: (c.edited || c.locked) ? c.price : market };
          }),
        };
      });
      return { ...plan, buckets: resynced };
    });
  }, [updateActive, dynamicPrices]);

  // ── Plan management ───────────────────────────────────────────────────────
  const newPlan = useCallback((name) => {
    const plan = seedPlan(BUDGET, name || "New Plan");
    setPlans(prev => [...prev, plan]);
    setActivePlanId(plan.id);
    setActiveCell(null);
    // ⚠️ OUTSIDE the setPlans updater, for the same reason the copy in
    // duplicatePlan is built outside its own: updaters must be pure, and
    // StrictMode runs them twice. A capture in there would double every count.
    // `plans` is the pre-add array, hence the + 1.
    capturePlanCreated({ source: "new", planCount: plans.length + 1 });
  }, [BUDGET, plans.length]);

  // 🔴 The copy is built OUTSIDE the setPlans updater on purpose. Updaters must
  // be pure — React invokes them twice in StrictMode — and newPlanId() inside
  // one mints two ids, so the plan that lands in state is not the one
  // setActivePlanId was handed. Compute outside, set inside. Same for
  // deletePlan below.
  const duplicatePlan = useCallback((name) => {
    const src = plans.find(p => p.id === activePlanId) || plans[0];
    if (!src) return;
    const copy = {
      ...src,
      id: newPlanId(),
      name: name || `${src.name} copy`,
      buckets: Object.fromEntries(
        Object.entries(src.buckets).map(([slotId, b]) => [slotId, { ...b, cells: b.cells.slice() }])
      ),
    };
    setPlans(prev => [...prev, copy]);
    setActivePlanId(copy.id);
    setActiveCell(null);
    capturePlanCreated({ source: "duplicate", planCount: plans.length + 1 });
  }, [plans, activePlanId]);

  /**
   * Create one or more plans from the ACTIVE plan with solver assignments
   * written in. Returns the new ids.
   *
   * 🔴 ONE ACTION FOR ALL OF THEM, ON PURPOSE. The optimizer produces two plans
   * from the SAME starting point. Doing that as duplicate → activate → fill,
   * twice, would build the second plan on top of the first: every mutation
   * action here targets the active plan, so the PPG result would become the
   * VORP run's starting roster. Taking all the specs at once means both are cut
   * from one source and activation happens exactly once, at the end.
   *
   * Each spec carries `columns`: an array of assignment lists, one per column.
   * Column c lands in CELL c of every slot as an ordinary Target, and the slot's
   * budget is deliberately left null — the optimizer already chose the spend, so
   * an explicit earmark would duplicate it and then drift the moment the board
   * moves. Columns the solver did not produce are left exactly as they were.
   *
   * 🔴 AN ASSIGNMENT MAY ARRIVE MARKED `edited`, and it must be honoured. The
   * solver prices candidates from YOUR sheet where you have set one, so a cell it
   * fills at your number is carrying your number — and writing it as edited:false
   * would let the very next resyncMarket overwrite it with the board price. The
   * plan would then disagree with the sheet that produced it, silently, seconds
   * after the button was pressed.
   *
   * 🔴 stampFingerprint, WHEN GIVEN, IS CALLED ON THE BUILT PLAN — not on the one
   * you started from. The fingerprint hashes every target's price, and this
   * function is what puts the solver's players into those cells, so a stamp taken
   * before the write describes a plan that never existed and the result would be
   * born reading "prices changed". This is the only correct place to take it.
   */
  const createFilledPlans = useCallback((specs, activateIndex = 0, stampFingerprint = null) => {
    if (!Array.isArray(specs) || specs.length === 0) return [];
    const src = plans.find(p => p.id === activePlanId) || plans[0];
    if (!src) return [];

    // Generated names are composed HERE because this is where `plans` lives —
    // the letter has to be free across the whole list, and useOptimizeActions
    // cannot see it. A spec may still pass a literal `name`; the suffix form is
    // what the buttons use.
    // 🔴 USER-NAMED PLANS DO NOT RESERVE A LETTER. "Plan B (sleepers)" is a
    // name somebody chose, not a slot in the alphabet, and letting it hold B
    // would push generated plans further down the alphabet every time one was
    // claimed. Duplicates are allowed anyway — plans are keyed by id.
    const takenNames = plans.filter((p) => !isUserNamed(p)).map((p) => ({ name: planBaseName(p.name) }));
    const nameFor = (spec) => {
      if (spec.name) return spec.name;
      const letter = nextPlanName(takenNames);
      takenNames.push({ name: letter });
      return spec.nameSuffix ? `${letter} ${spec.nameSuffix}` : letter;
    };

    const made = specs.map(spec => {
      const nextBuckets = {};
      Object.entries(src.buckets).forEach(([slotId, b]) => {
        nextBuckets[slotId] = { ...b, cells: b.cells.slice() };
      });
      // spec.columns[c] is a whole roster; it lands in cell c of every slot, so
      // reading DOWN column c is that roster and reading across a row is the
      // options at that slot. Columns beyond what the solver produced are left
      // exactly as they were.
      (spec.columns || []).forEach((assignments, cellIdx) => {
        if (cellIdx >= BUCKET_WIDTH) return;
        (assignments || []).forEach(a => {
          const base = nextBuckets[a.slotId] || emptyBucket();
          const cells = base.cells.slice();
          cells[cellIdx] = {
            ...makeTarget(a.player, dynamicPrices),
            price: Math.max(0, Number(a.price) || 0),
            edited: a.edited === true,
          };
          nextBuckets[a.slotId] = { ...base, cells };
        });
      });
      const built = {
        id: newPlanId(),
        name: nameFor(spec),
        budget: src.budget,
        objective: planObjective(src),
        budgetMode: planBudgetMode(src),
        buckets: nextBuckets,
        optimized: spec.optimized || null,
      };
      // Stamped here, from the finished plan. See the note above.
      if (!stampFingerprint || !built.optimized) return built;
      return { ...built, optimized: { ...built.optimized, fingerprint: stampFingerprint(built) } };
    });

    // 🔴 REPLACE, DO NOT APPEND. These plans are DERIVED — regenerating one
    // costs ~7ms — so they must not pile up like hand-built work. Ten clicks
    // of Optimize remaining used to leave twenty plans in the dropdown. A
    // re-run of the same mode+objective now overwrites its own previous
    // output, KEEPING ITS ID so the dropdown position is stable and an active
    // plan stays active.
    //
    // Computed outside the updater on purpose: the id depends on whether a
    // match was found, and setPlans updaters must stay pure (React runs them
    // twice in StrictMode).
    const next = plans.slice();
    const landed = [];
    made.forEach((built) => {
      const key = generatedKey(built);
      // 🔴 A CLAIMED PLAN IS NOT A RE-RUN TARGET. Replacement keys on
      // mode|objective, so without the isGenerated test here a renamed plan
      // would keep its name and have its CONTENTS overwritten by the next press
      // of the same button — the same loss as the sweep, wearing the right
      // label. A re-run now lands beside it as a new plan instead.
      const idx = key === null ? -1 : next.findIndex((p) => generatedKey(p) === key && isGenerated(p));
      if (idx >= 0) {
        // 🔴 THE NAME IS KEPT ALONG WITH THE ID, and for the same reason. A
        // re-run replaces its own previous output, so recomposing the name would
        // hand it the next FREE letter — a different one, because the plan it is
        // replacing still holds the old one — and the plan in the dropdown would
        // rename itself on every click of a button that changed nothing else.
        const replaced = { ...built, id: next[idx].id, name: next[idx].name };
        next[idx] = replaced;
        landed.push(replaced);
      } else {
        next.push(built);
        landed.push(built);
      }
    });

    setPlans(next);
    const target = landed[activateIndex] || landed[0];
    setActivePlanId(target.id);
    setActiveCell(null);
    return landed.map(p => p.id);
  }, [plans, activePlanId, dynamicPrices]);

  /**
   * Remove every optimizer-generated plan — the one-click cleanup for the pile
   * that accumulated before re-runs started replacing.
   *
   * 🔴 It can never take a hand-built plan, because it filters on the same
   * `optimized` flag the replacement keys on. A plan a person built by hand and
   * named "Optimized (PPG)" carries no flag and is invisible to this.
   *
   * If that would empty the list, a fresh plan is seeded rather than leaving
   * zero: "no plans at all" is not a reachable state anywhere else in this hook
   * and should not become one here.
   */
  const deleteGeneratedPlans = useCallback(() => {
    const kept = plans.filter((p) => !isGenerated(p));
    const removed = plans.length - kept.length;
    // 🔴 Returns the COUNT, including 0. The caller needs to tell "swept three"
    // from "there was nothing to sweep" — a bare early return made the button a
    // silent no-op, the same failure the fully-committed optimize path had.
    if (removed === 0) return 0;
    const next = kept.length > 0 ? kept : [seedPlan(BUDGET)];
    setPlans(next);
    setActivePlanId((cur) => (next.some((p) => p.id === cur) ? cur : next[0].id));
    setActiveCell(null);
    return removed;
  }, [plans, BUDGET]);

  /**
   * Keep the plan you are looking at; delete every other one.
   *
   * 🔴 NO HEURISTIC, AND THAT IS THE POINT. deleteGeneratedPlans can only see
   * plans carrying the `optimized` flag, so it is blind to every plan created
   * before that flag existed — which is every plan a current user already has.
   * Loosening its filter to match on NAME was the tempting fix and is the wrong
   * one: that rule protects hand-built work and is still right.
   *
   * This needs no rule at all. The user is looking at the plan they want and
   * says so, so provenance never enters into it — legacy piles, mixed piles and
   * future piles all collapse the same way.
   *
   * Refuses at one plan, and returns the count removed so the caller can name
   * what went rather than guessing.
   */
  const keepOnlyActivePlan = useCallback(() => {
    if (plans.length <= 1) return 0;
    const keepId = activePlanId && plans.some((p) => p.id === activePlanId)
      ? activePlanId
      : plans[0].id;
    const kept = plans.filter((p) => p.id === keepId);
    setPlans(kept);
    setActivePlanId(kept[0].id);
    setActiveCell(null);
    return plans.length - kept.length;
  }, [plans, activePlanId]);

  /**
   * Name a plan, and in doing so CLAIM it.
   *
   * 🔴 THE `userNamed` FLAG IS THE POINT, not the string. It takes the plan out
   * of isGenerated, so the picker stops filing it under Optimized and — the
   * part that matters — the bulk sweep stops being able to delete it. See
   * isGenerated for what that was one click away from doing.
   *
   * 🔴 BLANK IS A NO-OP, NOT A BLANK NAME. Trimmed empty means the user cleared
   * the field and moved on, which is a cancel however it reaches us; saving it
   * would leave an unidentifiable row in the picker that can only be fixed by
   * renaming a plan you can no longer tell apart. The component guards this too
   * — this is the guard that holds for every other caller.
   *
   * Duplicates are allowed. Plans are keyed by id, and two plans called
   * "sleepers" is a thing somebody may reasonably want.
   *
   * It does NOT touch id, buckets, objective or optimized — a name is not an
   * input to the solve, so the fingerprint cannot move and the plan does not
   * become stale for having been named.
   */
  const renamePlan = useCallback((name) => {
    const trimmed = String(name ?? "").trim();
    if (!trimmed) return;
    updateActive(plan => (plan.name === trimmed && plan.userNamed
      ? plan
      : { ...plan, name: trimmed, userNamed: true }));
  }, [updateActive]);

  // Refuses to delete the last plan: "no plans at all" is not a reachable state.
  const deletePlan = useCallback((id) => {
    if (plans.length <= 1) return;
    const next = plans.filter(p => p.id !== id);
    if (next.length === plans.length) return;
    setPlans(next);
    setActivePlanId(cur => (cur === id ? next[0].id : cur));
    setActiveCell(null);
  }, [plans]);

  /**
   * Pick what the optimizer maximises for this plan.
   *
   * 🔴 IT DOES NOT RE-SOLVE. Changing the objective changes what the NEXT run
   * optimises for, and — because the objective is part of the plan's staleness
   * fingerprint — makes the Rebuild button say so. Auto-solving on a toggle
   * would throw away the plan you are looking at on a stray click.
   */
  const setPlanObjective = useCallback((objective) => {
    const next = OBJECTIVES.includes(objective) ? objective : DEFAULT_OBJECTIVE;
    updateActive(plan => (planObjective(plan) === next ? plan : { ...plan, objective: next }));
  }, [updateActive]);

  /**
   * Pick what the money constraint is for this plan.
   *
   * 🔴 IT DOES NOT RE-SOLVE, for the reason setPlanObjective does not. The
   * mode is part of the staleness fingerprint, so flipping it makes Rebuild
   * say the plan no longer matches its own rules rather than silently
   * throwing away the plan on screen.
   */
  const setPlanBudgetMode = useCallback((mode) => {
    const next = BUDGET_MODES.includes(mode) ? mode : DEFAULT_BUDGET_MODE;
    updateActive(plan => (planBudgetMode(plan) === next ? plan : { ...plan, budgetMode: next }));
  }, [updateActive]);

  const setPlanBudget = useCallback((n) => {
    updateActive(plan => ({ ...plan, budget: Math.max(0, Number(n) || 0) }));
  }, [updateActive]);

  const clearPlan = useCallback(() => {
    updateActive(plan => ({ ...plan, buckets: {} }));
    setActiveCell(null);
  }, [updateActive]);

  // ── Export / import ───────────────────────────────────────────────────────
  const exportPlans = useCallback(() => ({ v: 1, plans }), [plans]);

  // Ids are regenerated on the way in: an import must never be able to collide
  // with — or silently overwrite — a plan the user already has.
  const importPlans = useCallback((obj) => {
    if (!obj || !Array.isArray(obj.plans)) return false;
    // Entry point 2 of 2. normalizePlan keeps an incoming id when it has one,
    // so the fresh newPlanId() AFTER it is load-bearing and must survive any
    // future edit here: an import must never collide with a plan the user has.
    const incoming = obj.plans.map(p => ({ ...normalizePlan(p, BUDGET), id: newPlanId() }));
    if (incoming.length === 0) return false;
    setPlans(prev => [...prev, ...incoming]);
    setActivePlanId(incoming[0].id);
    setActiveCell(null);
    return true;
  }, [BUDGET]);

  return {
    // state
    plans, activePlan, activePlanId, setActivePlanId, buckets,
    activeCell, setActiveCell,
    // derived
    leaguePrices, setSheetPrice, clearSheetPrices,
    leaguePpg, setSheetPpg, clearSheetPpg,
    leagueStars, toggleStar, clearStars,
    primaryFor, planned, targeted, allocated, secured, targetedOpen, lockedSlots,
    filled, committed,
    unplanned,
    emptySlots, remaining, maxBid, spendingByPos, byGroup,
    // target actions
    addTarget, removeTarget, promoteTarget, toggleGone, toggleLock, setTargetPrice,
    setSlotBudget, fillSlotBudgetsFromTargets, resyncMarket,
    // plan management
    newPlan, duplicatePlan, renamePlan, deletePlan, setPlanBudget, setPlanObjective, setPlanBudgetMode, clearPlan,
    createFilledPlans, deleteGeneratedPlans, keepOnlyActivePlan,
    exportPlans, importPlans,
  };
}
